import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import { useId, useState } from 'react';

import Display from '@/components/typography/Display';
import { Container, Section } from '@/components/ui/Section';

export type FAQItem = {
  question: string;
  answer: string;
};

export type FAQFooterLink = {
  prefix: string;
  linkLabel: string;
  suffix?: string;
  href: string;
};

type FAQSectionProps = {
  title: string;
  subtitle?: string;
  items: FAQItem[];
  footerLink?: FAQFooterLink;
  className?: string;
};

export default function FAQSection({
  title,
  subtitle,
  items,
  footerLink,
  className,
}: FAQSectionProps) {
  const idBase = useId();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <Section className={className}>
      <Container className="max-w-4xl">
        <div className="text-center space-y-4">
          <Display
            variant="h1"
            as="h2"
            className="text-3xl sm:text-4xl md:text-5xl text-white"
          >
            {title}
          </Display>
          {subtitle ? (
            <p className="text-base sm:text-lg text-white/90">{subtitle}</p>
          ) : null}
        </div>

        <div className="mt-12 rounded-3xl border border-white/15 bg-black/70 backdrop-blur-xl shadow-glass overflow-hidden">
          <div className="divide-y divide-white/10">
            {items.map((item, index) => {
              const isOpen = openIndex === index;
              const buttonId = `${idBase}-button-${index}`;
              const panelId = `${idBase}-panel-${index}`;

              return (
                <div key={buttonId}>
                  <button
                    id={buttonId}
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between gap-6 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                  >
                    <span className="text-base md:text-lg font-semibold">
                      {item.question}
                    </span>
                    <ChevronDown
                      className={clsx(
                        "h-5 w-5 text-white/60 transition-transform duration-300",
                        isOpen && "rotate-180 text-white"
                      )}
                      aria-hidden="true"
                    />
                  </button>
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    aria-hidden={!isOpen}
                    className={clsx(
                      "grid transition-[grid-template-rows,opacity] duration-300 ease-out",
                      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    )}
                  >
                    <div className="overflow-hidden">
                      <div className="px-6 pb-6">
                        <p className="text-sm text-white/90 leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {footerLink ? (
          <p className="mt-6 text-center text-sm text-white/90">
            {footerLink.prefix}
            <a
              href={footerLink.href}
              className="text-white hover:text-brand-highlight transition-colors underline underline-offset-2"
            >
              {footerLink.linkLabel}
            </a>
            {footerLink.suffix ?? ''}
          </p>
        ) : null}
      </Container>
    </Section>
  );
}
